// @ts-nocheck
import CarouselButton, {
    CarouselButtonPresentationProps,
    CarouselButtonProps,
} from "@store/Library/Components/Toolkit/Carousel/CarouselButton";
import ArrowBackward from "@store/Library/Components/Toolkit/Icons/ArrowBackward";
import ArrowDownward from "@store/Library/Components/Toolkit/Icons/ArrowDownward";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import ArrowUpward from "@store/Library/Components/Toolkit/Icons/ArrowUpward";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpointValues, { BreakpointValueProp } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import getCleanBreakpointValues from "@toolkit/Framework/Common/Utilities/getCleanBreakpointValues";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import React, { FC, HTMLAttributes, useMemo } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import CaretDown from "@store/Library/Components/Icons/CaretDown";
import CaretLeft from "@store/Library/Components/Icons/CaretLeft";
import CaretRight from "@store/Library/Components/Icons/CaretRight";
import CaretUp from "@store/Library/Components/Icons/CaretUp";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
/* Store Custom Code - End */

export interface CarouselArrowIcons {
    up: CarouselButtonPresentationProps;
    right: CarouselButtonPresentationProps;
    down: CarouselButtonPresentationProps;
    left: CarouselButtonPresentationProps;
}

export type CarouselArrowDirection = keyof CarouselArrowIcons;
export type CarouselArrowPosition = "top" | "right" | "bottom" | "left";

const getDirectionFromPosition = (position: CarouselArrowPosition | undefined): CarouselArrowDirection | undefined => {
    return position === "top" ? "up" : position === "bottom" ? "down" : position;
};

export interface CarouselArrowPresentationProps extends ExpandStyles<CarouselArrowStyles> {
    direction?: CarouselArrowDirection;
    icons?: CarouselArrowIcons;
    position?: CarouselArrowPosition;
    positionOffset?: BreakpointValueProp;
    positionOrigin?: BreakpointValueProp;
}

export interface CarouselArrowProps extends CarouselArrowPresentationProps, HTMLAttributes<HTMLDivElement> {
    carouselButton: CarouselButtonProps;
}

export interface CarouselArrowStyles {
    carouselButton?: CarouselButtonPresentationProps;
}

export const carouselArrowStyles: CarouselArrowStyles = {
    carouselButton: {
        iconSize: pxToRem(32), // Store Custom Code
        extendedStyles: {
            button: {
                css: css`
                    &:not(:disabled) {
                        color: ${getColor("text.link")}; /* Store Custom Code */

                        &:hover,
                        &:active {
                            color: ${getColor("primary")}; /* Store Custom Code */
                        }
                    }

                    ${IconWrapper} {
                        ${iconScalableStyles("0.9375em")}/* Store Custom Code */
                    }
                `,
            },
        },
    },
};

const CarouselArrow: FC<CarouselArrowProps> = ({
    carouselButton,
    children,
    direction: baseDirection,
    extendedStyles,
    icons,
    overridedStyles,
    position,
    positionOffset: basePositionOffset,
    positionOrigin: basePositionOrigin,
    ...htmlAttributes
}) => {
    const direction = baseDirection || getDirectionFromPosition(position);
    const isVertical = position === "top" || position === "bottom";
    const positionOffset = getCleanBreakpointValues(basePositionOffset);
    const positionOrigin = getCleanBreakpointValues(basePositionOrigin);
    const styles = useExpandProps(carouselArrowStyles, extendedStyles, overridedStyles);

    const carouselButtonStyles = useMemo(() => {
        return mergeProps<CarouselArrowStyles["carouselButton"]>(styles.carouselButton, {
            extendedStyles: {
                wrapper: {
                    css: css`
                        ${() => {
                            if (position) {
                                const originSign = position === "top" || position === "left" ? "-" : "";

                                return css`
                                    position: absolute;
                                    ${isVertical ? "left" : "top"}: 50%;
                                    ${position}: 0;

                                    ${breakpointValues(
                                        positionOffset?.map(x => {
                                            if (!x && x !== 0) {
                                                return null;
                                            }

                                            return css`
                                                ${`margin-${position}`}: ${x};
                                            `;
                                        }),
                                    )}

                                    ${breakpointValues(
                                        positionOrigin?.map(x => {
                                            if (!x && x !== 0) {
                                                return null;
                                            }

                                            return css`
                                                transform: translate3d(
                                                    ${isVertical
                                                        ? `-50%, ${originSign}${x}`
                                                        : `${originSign}${x}, -50%`},
                                                    0
                                                );
                                            `;
                                        }),
                                    )}
                                `;
                            }
                        }}
                    `,
                },
            },
        });
    }, [position, positionOffset, positionOrigin, styles.carouselButton]);

    return (
        <CarouselButton
            {...(direction && icons?.[direction])}
            {...carouselButtonStyles}
            {...carouselButton}
            {...htmlAttributes}
            className={`arrow${isVertical ? " vertical" : " horizontal"}${
                carouselButton.className ? ` ${carouselButton.className}` : ""
            }`}
        />
    );
};

CarouselArrow.defaultProps = {
    icons: {
        /* Store Custom Code - Start */
        up: { iconSrc: CaretUp, iconMargin: "-0.07em 0 0 0" },
        right: { iconSrc: CaretRight, iconMargin: "0 -0.07em 0 0" },
        down: { iconSrc: CaretDown, iconMargin: "0 0 -0.07em 0" },
        left: { iconSrc: CaretLeft, iconMargin: "0 0 0 -0.07em" },
        /* Store Custom Code - End */
    },
};

export default CarouselArrow;
